import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppButton, Box, FlexControlledForm, Preloader, Text } from '@common-fe/common-fe';
import * as yup from 'yup';

import { ValuesMap } from '@/common/types';

import { InvestmentPaths, SelfDirectedInvestmentsQuestionnairePayload } from '../../investments.types';
import ServerErrorModal from '../../modal/ServerErrorModal';
import { useInvestmentQuestionnaireQuery } from '../../queries';
import QuestionnaireHint from '../../QuestionnaireHint';

import {
  useDefaultQuestionnaireValues,
  useInvestmentsProfileQuestionnaireFields,
  useQuestionnaireFields,
} from './hooks';
interface Props {
  onSubmit: () => void;
  onBack: () => void;
  investmentType: InvestmentPaths;

  isSwitchMode?: boolean;
}

const QuestionnaireFields: React.FC<Props> = ({ isSwitchMode, onSubmit, investmentType, onBack }) => {
  const [isShowErrors, setShowErrors] = useState(false);
  const [isServerError, setServerError] = useState(false);
  const { t } = useTranslation();

  const defaultValues = useDefaultQuestionnaireValues();

  const [localValues, setLocalValues] = useState<Partial<SelfDirectedInvestmentsQuestionnairePayload>>(
    defaultValues || {}
  );
  const handleSetLocalValues = useCallback(
    (values: Partial<SelfDirectedInvestmentsQuestionnairePayload>) => {
      setLocalValues({
        ...localValues,
        ...values,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      localValues?.annualIncome,
      localValues?.networthTotal,
      localValues?.position,
      localValues?.broker,
      localValues?.employmentType,
      localValues?.investmentExperienceType,
      localValues?.investmentObjectivesType,
    ]
  );
  useEffect(() => {
    setLocalValues(defaultValues);
  }, [defaultValues]);

  const fields = useQuestionnaireFields({
    defaultValues,
  });
  const investmentFields = useInvestmentsProfileQuestionnaireFields({
    defaultValues,
  });
  const { handleSave, isLoading } = useInvestmentQuestionnaireQuery({ investmentType: investmentType });

  const handleSubmit = useCallback(async () => {
    if (isLoading) {
      return;
    }

    setShowErrors(true);
    const validatorMap = [...fields, ...investmentFields].reduce((result, field) => {
      return {
        ...result,
        [field.name]: field.validator,
      } as ValuesMap<yup.AnySchema>;
    }, {});

    const schema = yup.object().shape(validatorMap);
    try {
      schema.validateSync(localValues, { abortEarly: false });

      try {
        await handleSave(localValues as SelfDirectedInvestmentsQuestionnairePayload);
        setShowErrors(false);
        onSubmit();
      } catch {
        setServerError(true);
      }
    } catch (e) {
      setShowErrors(true);
    }
  }, [fields, handleSave, investmentFields, isLoading, localValues, onSubmit]);

  const nextButtonText = useMemo(() => {
    if (isSwitchMode) {
      return t('Next (1/3)');
    }
    return t('Next (2/4)');
  }, [isSwitchMode, t]);
  return (
    <>
      <ServerErrorModal
        visible={isServerError}
        isLoading={isLoading}
        onSetVisible={setServerError}
        onSendRequest={handleSubmit}
      />
      <Box direction="column">
        <Text size="3xl" weight={700}>
          {t('Portfolio Questionnaire')}
        </Text>
        <Box
          direction="column"
          justify="start"
          align="start"
          margin={{ top: 'l' }}
          style={{ position: 'relative' }}
        >
          <Text size="20px" weight={700}>
            {t('Personal Information')}
          </Text>

          <Box direction="column" fill margin={{ top: 'spacing8' }}>
            <QuestionnaireHint />
          </Box>
        </Box>

        <Box margin={{ top: 's' }} background="module" pad="l" round="moduleRound">
          <Box direction="column">
            {fields.map((field) => (
              <Box
                key={field.name}
                background="canvas"
                elevation="default"
                border={{ color: 'border2' }}
                round="container1Round"
                margin={{ bottom: 'l' }}
              >
                <FlexControlledForm
                  fields={[field]}
                  showError={isShowErrors}
                  editMode
                  onChangeValues={handleSetLocalValues}
                />
              </Box>
            ))}
          </Box>
        </Box>
        <Box direction="column" margin={{ top: 'spacing24' }}>
          <Text size="20px" weight={700}>
            {t('Investment Profile')}
          </Text>
          <Box margin={{ top: 's' }} background="module" pad="l" round="moduleRound">
            <Box direction="column" style={{ position: 'relative' }}>
              <Box direction="column">
                {investmentFields.map((field) => (
                  <Box
                    key={field.name}
                    background="canvas"
                    elevation="default"
                    border={{ color: 'border2' }}
                    round="container1Round"
                    margin={{ bottom: 'l' }}
                  >
                    <FlexControlledForm
                      fields={[field]}
                      showError={isShowErrors}
                      editMode
                      onChangeValues={handleSetLocalValues}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>

        <Box direction="row" justify="end" margin={{ top: 'l', bottom: 'l' }}>
          <AppButton width="140px" testId="investments_back" buttonType="secondary" onClick={onBack}>
            {t('Back')}
          </AppButton>
          <Box margin={{ left: 's' }}>
            <AppButton testId="investments_next" width="140px" disabled={isLoading} onClick={handleSubmit}>
              {isLoading ? <Preloader /> : nextButtonText}
            </AppButton>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default QuestionnaireFields;
